import { createSiweMessage } from 'viem/siwe'
import { env } from '../env'

/*
 * Sign-in with Ethereum as a small state machine, kept pure so it can be
 * tested without a wallet: idle → connecting → (network, when the wallet is
 * on another chain) → nonce → signing → verifying → done, with typed
 * failures that return to a retryable state.
 */

export type SiweStep = 'idle' | 'connecting' | 'network' | 'nonce' | 'signing' | 'verifying' | 'done' | 'error'
export type SiweErrorKind = 'rejected' | 'pending' | 'locked' | 'unsupported-chain' | 'no-wallet' | 'unknown'

export interface SiweState {
  step: SiweStep
  address?: `0x${string}` | undefined
  /** step that failed, for retry and copy */
  failedAt?: Exclude<SiweStep, 'idle' | 'done' | 'error'> | undefined
  error?: string | undefined
  /** user declined in the wallet: not an error, just stop */
  rejected?: boolean | undefined
  /** what kind of failure, for copy and recovery actions */
  kind?: SiweErrorKind | undefined
  /** the wallet's chain while on the `network` step */
  walletChainId?: number | undefined
  /** a switch request is open in the wallet */
  switching?: boolean | undefined
}

export type SiweEvent =
  | { type: 'CONNECT' }
  | { type: 'CONNECTED'; address: `0x${string}` }
  | { type: 'WRONG_NETWORK'; address: `0x${string}`; chainId: number }
  | { type: 'SWITCHING' }
  | { type: 'START'; address: `0x${string}` }
  | { type: 'NONCE_OK' }
  | { type: 'SIGNED' }
  | { type: 'VERIFIED' }
  | { type: 'FAIL'; error: string; rejected?: boolean; kind?: SiweErrorKind }
  | { type: 'RESET' }

export const initialSiwe: SiweState = { step: 'idle' }

export function siweReducer(s: SiweState, e: SiweEvent): SiweState {
  switch (e.type) {
    case 'RESET':
      return initialSiwe
    case 'CONNECT':
      return s.step === 'idle' || s.step === 'error' ? { step: 'connecting' } : s
    case 'WRONG_NETWORK':
      if (s.step === 'idle' || s.step === 'connecting' || s.step === 'error' || s.step === 'network') return { step: 'network', address: e.address, walletChainId: e.chainId }
      return s
    case 'SWITCHING':
      return s.step === 'network' ? { ...s, switching: true } : s
    case 'CONNECTED':
    case 'START':
      if (s.step === 'idle' || s.step === 'connecting' || s.step === 'error' || s.step === 'network') return { step: 'nonce', address: e.address }
      return s
    case 'NONCE_OK':
      return s.step === 'nonce' ? { ...s, step: 'signing' } : s
    case 'SIGNED':
      return s.step === 'signing' ? { ...s, step: 'verifying' } : s
    case 'VERIFIED':
      return s.step === 'verifying' ? { ...s, step: 'done' } : s
    case 'FAIL':
      if (s.step === 'idle' || s.step === 'done' || s.step === 'error') return s
      return { step: 'error', address: s.address, failedAt: s.step, error: e.error, rejected: e.rejected ?? false, kind: e.kind ?? (e.rejected ? 'rejected' : 'unknown'), ...(s.walletChainId !== undefined ? { walletChainId: s.walletChainId } : {}) }
  }
}

export interface SiweDeps {
  getNonce: (address: `0x${string}`) => Promise<{ nonce: string; issuedAt: string; expiresAt: string; domain?: string | undefined; chainId?: number | undefined }>
  sign: (message: string) => Promise<`0x${string}`>
  verify: (message: string, signature: `0x${string}`) => Promise<unknown>
  domain: string
  uri: string
  /** chain id for the message when the API does not say (default: the configured VITE_CHAIN_ID) */
  chainId?: number
  now?: () => Date
}

/**
 * Build the EIP-4361 message the API verifies (domain, chain id and nonce checked). The chain id is the
 * one the API announced with the nonce, else the configured `VITE_CHAIN_ID` (4663 by default, 31337 on anvil).
 */
export function buildSiweMessage(p: { address: `0x${string}`; nonce: string; domain: string; uri: string; issuedAt: Date; expiresAt?: Date; chainId?: number | undefined }): string {
  return createSiweMessage({
    address: p.address,
    chainId: p.chainId ?? env.chainId,
    domain: p.domain,
    nonce: p.nonce,
    uri: p.uri,
    version: '1',
    issuedAt: p.issuedAt,
    ...(p.expiresAt ? { expirationTime: p.expiresAt } : {}),
    statement: 'Sign in to algo.boo.',
  })
}

function isRejection(err: unknown): boolean {
  const e = err as { code?: number; name?: string; message?: string; cause?: { code?: number } }
  return e?.code === 4001 || e?.cause?.code === 4001 || e?.name === 'UserRejectedRequestError' || /reject|denied|cancel/i.test(e?.message ?? '')
}

function isPending(err: unknown): boolean {
  const e = err as { code?: number; message?: string; cause?: { code?: number } }
  return e?.code === -32002 || e?.cause?.code === -32002 || /already pending/i.test(e?.message ?? '')
}

function message(err: unknown): string {
  const e = err as { shortMessage?: string; message?: string }
  return e?.shortMessage ?? e?.message ?? 'Something went wrong'
}

/** Drive the flow from `nonce` to `done`, dispatching events along the way. */
export async function runSiwe(address: `0x${string}`, deps: SiweDeps, dispatch: (e: SiweEvent) => void): Promise<boolean> {
  dispatch({ type: 'START', address })
  let msg: string
  try {
    const n = await deps.getNonce(address)
    // The API names the domain and chain it verifies; sign exactly those (a mismatch could never verify).
    msg = buildSiweMessage({ address, nonce: n.nonce, domain: n.domain ?? deps.domain, uri: deps.uri, issuedAt: deps.now?.() ?? new Date(), expiresAt: new Date(n.expiresAt), chainId: n.chainId ?? deps.chainId })
    dispatch({ type: 'NONCE_OK' })
  } catch (err) {
    dispatch({ type: 'FAIL', error: message(err) })
    return false
  }
  let sig: `0x${string}`
  try {
    sig = await deps.sign(msg)
    dispatch({ type: 'SIGNED' })
  } catch (err) {
    dispatch({ type: 'FAIL', error: isRejection(err) ? 'You declined the signature in your wallet.' : message(err), rejected: isRejection(err), kind: isRejection(err) ? 'rejected' : isPending(err) ? 'pending' : 'unknown' })
    return false
  }
  try {
    await deps.verify(msg, sig)
    dispatch({ type: 'VERIFIED' })
    return true
  } catch (err) {
    dispatch({ type: 'FAIL', error: message(err) })
    return false
  }
}
